import type { BaseItemSchema } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import type { InlineBlankField } from "@memorang/ui/components/StudySession/SessionContent/InlineBlankFields";
import FillInTheBlanksDropdown from "@memorang/ui/components/StudySession/SessionContent/item-types/FillInTheBlanksDropdown";
import { useCallback, useMemo } from "react";
import {
	getBlankValuesResponse,
	toPersistableBlankValues,
} from "../../../helpers/ai-feedback";
import { normalizeBlankStem } from "../../../helpers/blank-tokens";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import useItemAnsweredState from "../../../hooks/useItemAnsweredState";
import DesktopFeedbackPanel from "./DesktopFeedbackPanel";

type Props = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
};

const EMPTY_BLANK_VALUES: Record<string, string> = {};

const FibDropdownContainer = ({ item, isSplitLayout }: Props) => {
	const { content, response, scoring } = item;
	const itemId = item.id!;

	const { isAnswered } = useItemAnsweredState(item);

	const setUserResponse = useAIFeedbackStore((s) => s.setUserResponse);
	const answeredValues = useAIFeedbackStore((s) =>
		isAnswered
			? (getBlankValuesResponse(s.userResponse, itemId) ??
				item.answer?.selectedBlankValues ??
				EMPTY_BLANK_VALUES)
			: undefined,
	);

	const defaultValues = useMemo(
		() =>
			getBlankValuesResponse(
				useAIFeedbackStore.getState().userResponse,
				itemId,
			) ??
			item.answer?.selectedBlankValues ??
			EMPTY_BLANK_VALUES,
		[itemId, item.answer?.selectedBlankValues],
	);

	const rawStem =
		content.stimulus?.type === "TEXT" ? (content.stimulus.text ?? "") : "";
	const stem = useMemo(() => normalizeBlankStem(rawStem), [rawStem]);

	const fields = useMemo((): InlineBlankField[] => {
		if (response.type !== "INLINE_OPTION_SELECTION") {
			return [];
		}
		return response.blanks.map((b) => ({
			blankId: b.blankId,
			choices: b.choices,
		}));
	}, [response]);

	const handleChangeValues = useCallback(
		(nextValues: Record<string, string>) => {
			setUserResponse(itemId, toPersistableBlankValues(nextValues));
		},
		[itemId, setUserResponse],
	);

	const instruction =
		response.type === "INLINE_OPTION_SELECTION"
			? (response.instruction ?? "")
			: "";

	const correctValues = useMemo((): Record<string, string> | undefined => {
		if (
			!(scoring && "type" in scoring) ||
			scoring.type !== "ANSWER_KEY_MAPPING"
		) {
			return undefined;
		}
		const map: Record<string, string> = {};
		for (const entry of scoring.answerKey) {
			if ("blankId" in entry && "choiceId" in entry) {
				map[entry.blankId] = entry.choiceId;
			}
		}
		return map;
	}, [scoring]);

	return (
		<Box gap={16}>
			<FillInTheBlanksDropdown
				instruction={instruction}
				stem={stem}
				fields={fields}
				defaultValues={defaultValues}
				values={isAnswered ? answeredValues : undefined}
				onChangeValues={handleChangeValues}
				disabled={isAnswered}
				correctValues={isAnswered ? correctValues : undefined}
			/>
			<DesktopFeedbackPanel isSplitLayout={isSplitLayout} />
		</Box>
	);
};

export default FibDropdownContainer;
